import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { apiPost } from "./api";
import "./tournament.css";

const AUTO_POOL_FACTOR = 0.7;
const money = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const nowLocal = (plusMin = 0) => {
  const d = new Date(Date.now() + plusMin * 60000);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const defaultRules = [
  "Tournament entry fee join ke time ek hi baar charge hogi.",
  "Har round ka Ludo match existing secure game engine par chalega; extra round entry nahi lagegi.",
  "Win par 3 tournament points milenge; loss par 0 points.",
  "Same points par Buchholz, wins, tokens home aur winning time tie-break me use honge.",
  "Registration close hone ke baad confirmed entries ke basis par final prize list lock hogi.",
  "Final leaderboard ke baad eligible prizes automatically wallet me credit honge.",
];

function suggestedBands(playerCount) {
  if (playerCount <= 2)
    return [
      { label: "Rank 1", pct: 70 },
      { label: "Rank 2", pct: 30 },
    ];
  if (playerCount < 10)
    return [
      { label: "Rank 1", pct: 50 },
      { label: "Rank 2", pct: 30 },
      { label: "Rank 3", pct: 20 },
    ];
  return [
    { label: "Rank 1", pct: 35 },
    { label: "Rank 2", pct: 20 },
    { label: "Rank 3", pct: 10 },
    { label: "Rank 4 - 10", pct: 35, places: 7 },
  ];
}

export default function TournamentCreate() {
  const history = useHistory();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    title: "Ludobro Ludo Championship",
    subtitle:
      "Multi-round Ludo tournament with live leaderboard & automatic prizes",
    entryFee: 100,
    maxPlayers: 100,
    minPlayers: 10,
    totalRounds: 5,
    sponsorPrizePool: 0,
    allowCpuFill: false,
    registrationClosesAt: nowLocal(60),
    startsAt: nowLocal(65),
    rules: defaultRules,
  });

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const maxPlayers = Math.max(2, Number(form.maxPlayers || 0));
  const entryFee = Math.max(0, Number(form.entryFee || 0));
  const maxCollection = maxPlayers * entryFee;
  const autoPrizePool =
    entryFee === 0
      ? Number(form.sponsorPrizePool || 0)
      : Math.floor(maxCollection * AUTO_POOL_FACTOR * 100) / 100;
  const reserve = Math.max(0, maxCollection - autoPrizePool);

  const prizeRows = useMemo(() => {
    return suggestedBands(maxPlayers).map((row) => {
      const bandAmount = Math.floor(autoPrizePool * row.pct) / 100;
      const each = row.places
        ? Math.floor((bandAmount / row.places) * 100) / 100
        : Math.floor(bandAmount * 100) / 100;
      return { ...row, each };
    });
  }, [maxPlayers, autoPrizePool]);

  async function save() {
    try {
      setSaving(true);
      setError("");
      const body = {
        ...form,
        entryFee,
        maxPlayers,
        minPlayers: Math.min(
          Math.max(2, Number(form.minPlayers || 2)),
          maxPlayers,
        ),
        totalRounds: Math.min(20, Math.max(1, Number(form.totalRounds || 1))),
        sponsorPrizePool: Number(form.sponsorPrizePool || 0),
        allowCpuFill: entryFee === 0 && Boolean(form.allowCpuFill),
        registrationClosesAt: new Date(form.registrationClosesAt).toISOString(),
        startsAt: new Date(form.startsAt).toISOString(),
      };
      const res = await apiPost("admin/tournaments", body);
      history.push(`/admin/tournaments/${res.data.data._id}`);
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="tour-page tour-create-page">
      <div className="tour-head pro-head">
        <div>
          <div className="tour-eyebrow">TOURNAMENT STUDIO</div>
          <h1>Create Ludo Tournament</h1>
          <div className="tour-sub">
            Entry fee aur player capacity dalo — prize pool aur prize list
            automatically generate ho jayegi.
          </div>
        </div>
        <button className="tour-btn soft" onClick={() => history.goBack()}>
          ← Back
        </button>
      </div>

      <div className="tour-create-layout">
        <div className="tour-create-main">
          <section className="tour-card pro-card">
            <div className="tour-section-head">
              <span className="tour-section-no">01</span>
              <div>
                <h3>Tournament Identity</h3>
                <p>User ko card aur detail page par ye information dikhegi.</p>
              </div>
            </div>
            <div className="tour-form-grid">
              <div className="tour-field wide">
                <label>Tournament Name</label>
                <input
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                />
              </div>
              <div className="tour-field wide">
                <label>Short Description</label>
                <input
                  value={form.subtitle}
                  onChange={(e) => set("subtitle", e.target.value)}
                />
              </div>
            </div>
          </section>

          <section className="tour-card pro-card">
            <div className="tour-section-head">
              <span className="tour-section-no">02</span>
              <div>
                <h3>Entry & Capacity</h3>
                <p>Yahi do values auto prize pool ka main base hain.</p>
              </div>
            </div>
            <div className="tour-form-grid">
              <div className="tour-field">
                <label>Entry Fee</label>
                <div className="tour-input-prefix">
                  <span>₹</span>
                  <input
                    type="number"
                    min="0"
                    value={form.entryFee}
                    onChange={(e) => set("entryFee", e.target.value)}
                  />
                </div>
                <small>Per user, tournament join ke time only once.</small>
              </div>
              <div className="tour-field">
                <label>Maximum Players</label>
                <input
                  type="number"
                  min="2"
                  max="10000"
                  value={form.maxPlayers}
                  onChange={(e) => set("maxPlayers", e.target.value)}
                />
                <small>Example: 100 seats.</small>
              </div>
              <div className="tour-field">
                <label>Minimum Players To Start</label>
                <input
                  type="number"
                  min="2"
                  value={form.minPlayers}
                  onChange={(e) => set("minPlayers", e.target.value)}
                />
              </div>
              <div className="tour-field">
                <label>Total Rounds</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={form.totalRounds}
                  onChange={(e) => set("totalRounds", e.target.value)}
                />
                <small>Swiss-style leaderboard rounds.</small>
              </div>
              {entryFee === 0 && (
                <div className="tour-field wide">
                  <label>Sponsored Prize Pool</label>
                  <div className="tour-input-prefix">
                    <span>₹</span>
                    <input
                      type="number"
                      min="0"
                      value={form.sponsorPrizePool}
                      onChange={(e) => set("sponsorPrizePool", e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="tour-card pro-card">
            <div className="tour-section-head">
              <span className="tour-section-no">03</span>
              <div>
                <h3>Schedule</h3>
                <p>
                  Registration lock hote hi confirmed entries se final prize
                  list freeze hogi.
                </p>
              </div>
            </div>
            <div className="tour-form-grid">
              <div className="tour-field">
                <label>Registration Closes</label>
                <input
                  type="datetime-local"
                  value={form.registrationClosesAt}
                  onChange={(e) => set("registrationClosesAt", e.target.value)}
                />
              </div>
              <div className="tour-field">
                <label>Tournament Starts</label>
                <input
                  type="datetime-local"
                  value={form.startsAt}
                  onChange={(e) => set("startsAt", e.target.value)}
                />
              </div>
            </div>
            {entryFee === 0 && (
              <label className="tour-switch-row">
                <input
                  type="checkbox"
                  checked={form.allowCpuFill}
                  onChange={(e) => set("allowCpuFill", e.target.checked)}
                />
                <span>
                  <strong>Allow disclosed Expert CPU fill</strong>
                  <small>Sirf free/practice tournaments ke liye.</small>
                </span>
              </label>
            )}
          </section>

          <section className="tour-card pro-card">
            <div className="tour-section-head">
              <span className="tour-section-no">04</span>
              <div>
                <h3>How Players Will Play</h3>
                <p>
                  Ye same flow user tournament detail page par bhi clear steps
                  me dikhega.
                </p>
              </div>
            </div>
            <div className="tour-flow-grid">
              <div className="tour-flow-step">
                <b>1</b>
                <strong>Join</strong>
                <span>
                  User tournament join karega aur entry ek baar lock hogi.
                </span>
              </div>
              <div className="tour-flow-step">
                <b>2</b>
                <strong>Pairing</strong>
                <span>
                  Round start hote hi same-score players ka pairing auto banega.
                </span>
              </div>
              <div className="tour-flow-step">
                <b>3</b>
                <strong>Play Ludo</strong>
                <span>
                  User ko “Play Match” button milega jo existing /ludo/:id board
                  kholega.
                </span>
              </div>
              <div className="tour-flow-step">
                <b>4</b>
                <strong>Auto Result</strong>
                <span>
                  Game result server se standings me jayega; manual result nahi.
                </span>
              </div>
              <div className="tour-flow-step">
                <b>5</b>
                <strong>Next Round</strong>
                <span>
                  Round complete hote hi next pairing aur leaderboard update
                  hoga.
                </span>
              </div>
              <div className="tour-flow-step">
                <b>6</b>
                <strong>Prize</strong>
                <span>
                  Final ranking ke baad winning amount automatically wallet me
                  credit hogi.
                </span>
              </div>
            </div>
          </section>

          {error && <div className="tour-alert error">{error}</div>}
          <div className="tour-actions create-actions">
            <button
              disabled={saving}
              className="tour-btn gold large"
              onClick={save}
            >
              {saving ? "Creating Tournament…" : "Create Tournament Draft"}
            </button>
          </div>
        </div>

        <aside className="tour-create-side">
          <div className="tour-preview-card">
            <div className="preview-top">
              <div>
                <span className="preview-label">LIVE PREVIEW</span>
                <h3>{form.title || "Tournament"}</h3>
              </div>
              <span className="tour-badge gold">DRAFT</span>
            </div>
            <p>{form.subtitle}</p>

            <div className="preview-money-grid">
              <div>
                <small>Entry</small>
                <strong>{money(entryFee)}</strong>
              </div>
              <div className="highlight">
                <small>Auto Prize Pool</small>
                <strong>{money(autoPrizePool)}</strong>
              </div>
              <div>
                <small>Max Players</small>
                <strong>{maxPlayers}</strong>
              </div>
              <div>
                <small>Rounds</small>
                <strong>{form.totalRounds}</strong>
              </div>
            </div>

            <div className="tour-finance-strip">
              <div>
                <span>Max Collection</span>
                <strong>{money(maxCollection)}</strong>
              </div>
              <div>
                <span>Platform Reserve</span>
                <strong>{money(reserve)}</strong>
              </div>
            </div>

            <div className="preview-prize-title">
              <span>Auto Prize List</span>
              <small>full capacity preview</small>
            </div>
            <div className="preview-prizes">
              {prizeRows.map((r, index) => (
                <div
                  className={`preview-prize-row ${index < 3 ? "podium" : ""}`}
                  key={r.label}
                >
                  <span>
                    <i>{index === 0 ? "♛" : index + 1}</i>
                    {r.label}
                  </span>
                  <strong>
                    {money(r.each)}
                    {r.places ? " each" : ""}
                  </strong>
                </div>
              ))}
            </div>

            <div className="tour-alert info">
              Prize pool tournament publish hone se pehle hi generate ho jayegi.
              Registration ke time user ko max prize preview dikhega.
              Registration close hone par confirmed paid entries ke basis par
              final prize pool aur prize list automatically lock hogi.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
